import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { serverContext } from '@adornis/baseql/server/context.js';
import { runInContext } from '@adornis/baseql/server/server.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import { type MailTemplateDefinition, mailTemplateDefinitions } from '@adornis/mails/db/mail-template-definition.js';
import { switchMap, map, of } from 'rxjs';
import { getDbPluginForScope } from '../db-plugins.js';
import { Permission } from '../enums/roles.js';
import { mongo } from '../server/mongo.js';
import { GlobalMailTemplate, InstitutionMailTemplate, MailTemplateType } from '../entities/mail-templates.js';

const shortCache = (() => {
  const cache = new Map<string, { createdAt: number; value: any }>();
  return {
    get: async <T>(key: string, ttl: number, createDefault: () => Promise<T>): Promise<T> => {
      if (cache.has(key) && new Date().getTime() - cache.get(key)!.createdAt < ttl) {
        return cache.get(key)!.value;
      }

      const value = await createDefault();
      cache.set(key, { createdAt: new Date().getTime(), value });
      return value;
    },
  };
})();

async function getInstitutionMailTemplateFromDefinitionAndCreateIfNotExists(
  institutionID: string | undefined,
  type: string,
  definition: MailTemplateDefinition,
) {
  const TEN_SECONDS = 1000 * 10;

  const existingInstitutionMailTemplates = await shortCache.get(
    `institutionMailTemplates-${institutionID}`,
    TEN_SECONDS,
    () => {
      const c = mongo().collection<InstitutionMailTemplate>(InstitutionMailTemplate._collectionName);
      return c.find({ institutionID }).toArray();
    },
  );

  const existingInstitutionMailTemplate = existingInstitutionMailTemplates.find(
    mailTemplate => mailTemplate.emailType === type,
  );
  if (existingInstitutionMailTemplate) return existingInstitutionMailTemplate;

  const existingGlobalMailTemplates = await shortCache.get(`globalMailTemplates`, TEN_SECONDS, () => {
    const c = mongo().collection<GlobalMailTemplate>(GlobalMailTemplate._collectionName);
    return c.find({ _class: GlobalMailTemplate._class }).toArray();
  });

  const existingGlobalMailTemplate = existingGlobalMailTemplates.find(mailTemplate => mailTemplate.emailType === type);

  if (existingGlobalMailTemplate) {
    const newMailTemplate = new InstitutionMailTemplate({
      ...existingGlobalMailTemplate,
      _id: null,
      _class: InstitutionMailTemplate._class,
      institutionID,
    });
    await runInContext(serverContext.sessionID, () => newMailTemplate.create());
    return newMailTemplate;
  }

  const newMailTemplate = new InstitutionMailTemplate({
    emailType: type,
    institutionID,
    scope: 'default',
    name: definition.displayName,
    content: definition.defaultTemplate.content,
    subject: definition.defaultTemplate.subject,
    footer: definition.defaultTemplate.footer,
  });
  await runInContext(serverContext.sessionID, () => newMailTemplate.create());

  return newMailTemplate;
}

const getGlobalMailTemplatesResolver = function (this: typeof GlobalMailTemplate) {
  return async (gqlFields: BaseQLSelectionSet<GlobalMailTemplate>) => {
    const existingGlobalMailTemplates = await mongo()
      .collection<GlobalMailTemplate>(this._collectionName)
      .find({ _class: this._class })
      .toArray();

    const allTemplates = await Promise.all(
      Object.entries(mailTemplateDefinitions).map(async ([type, definition]) => {
        const existingGlobalMailTemplate = existingGlobalMailTemplates.find(
          mailTemplate => mailTemplate.emailType === type,
        );
        if (existingGlobalMailTemplate) {
          existingGlobalMailTemplate.scope = 'default';
          return existingGlobalMailTemplate;
        }

        const newMailTemplate = new GlobalMailTemplate({
          emailType: type,
          scope: 'default',
          name: definition.displayName,
          content: definition.defaultTemplate.content,
          subject: definition.defaultTemplate.subject,
          footer: definition.defaultTemplate.footer,
        });
        await newMailTemplate.create();

        return newMailTemplate;
      }),
    );

    return allTemplates;
  };
};

export const getGlobalMailTemplates = registerQuery({
  operationName: 'getGlobalMailTemplates',
  type: () => [GlobalMailTemplate],
  params: [],
  middlewares: [checkAuthorizedMiddleware(({ user }) => user.hasPermission(Permission.MANAGE_GLOBAL_MAIL_TEMPLATES))],
  resolve: (...args: Parameters<typeof getGlobalMailTemplatesResolver>) =>
    getGlobalMailTemplatesResolver.apply(GlobalMailTemplate, args),
});

export const getMailTemplatesForInstitution = registerQuery({
  operationName: 'getMailTemplatesForInstitution',
  type: () => [InstitutionMailTemplate],
  params: [{ name: 'institutionID', type: () => ID }],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) =>
      user.hasPermission(Permission.MANAGE_INSTITUTION, params.institutionID),
    ),
  ],
  resolve: (institutionID: string) => {
    return async (gqlFields: BaseQLSelectionSet<InstitutionMailTemplate>) => {
      if (!institutionID) throw new Error("'getMailTemplatesForInstitution' braucht eine institutionID");
      const allTemplates = await Promise.all(
        Object.entries(mailTemplateDefinitions).map(async ([type, definition]) => {
          return getInstitutionMailTemplateFromDefinitionAndCreateIfNotExists(institutionID, type, definition);
        }),
      );

      // filter out plugin emails
      return allTemplates.filter(template => {
        const mailType = template.emailType;
        const isPluginTemplate = mailType.startsWith('plugin::');
        if (!isPluginTemplate) return true;

        const paymentPlugin = getDbPluginForScope('payment', institutionID);
        return mailType.startsWith('plugin::' + paymentPlugin.id);
      });
    };
  },
});

export const getMailTemplateForInstitution = registerQuery({
  operationName: 'getMailTemplateForInstitution',
  type: () => InstitutionMailTemplate,
  params: [
    { name: 'institutionID', type: () => ID },
    { name: 'type', type: () => String },
  ],
  resolve: (institutionID: string | undefined, type: MailTemplateType) => {
    return async <SelSet extends BaseQLSelectionSet<InstitutionMailTemplate>>(gqlFields: SelSet) => {
      const definition = mailTemplateDefinitions[type];
      if (!definition) throw new Error('No definition found for type: ' + type);
      return getInstitutionMailTemplateFromDefinitionAndCreateIfNotExists(
        institutionID,
        type,
        definition,
      ) as Promise<InstitutionMailTemplate>;
    };
  },
});

export const subscribeMailTemplatesForInstitution = registerSubscription({
  operationName: 'subscribeMailTemplatesForInstitution',
  type: () => [InstitutionMailTemplate],
  params: [{ name: 'institutionID', type: () => String }],
  resolve: (institutionID: string) => {
    return (gqlFields: BaseQLSelectionSet<InstitutionMailTemplate>) => {
      if (!institutionID) throw new Error("'subscribeMailTemplatesForInstitution' braucht eine institutionID");

      return watchEntireCollection(InstitutionMailTemplate._collectionName).pipe(
        switchMap(() => getMailTemplatesForInstitution(institutionID)(gqlFields)),
      );
    };
  },
});

export const getInstitutionMailTemplatesByIDs = registerQuery({
  operationName: 'getInstitutionMailTemplatesByIDs',
  type: () => [InstitutionMailTemplate],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<InstitutionMailTemplate>>(gqlFields: SelSet) =>
      readEntitiesByIDs(InstitutionMailTemplate, ids, gqlFields),
});

export const subscribeInstitutionMailTemplatesByIDs = registerSubscription({
  operationName: 'subscribeInstitutionMailTemplatesByIDs',
  type: () => [InstitutionMailTemplate],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<InstitutionMailTemplate>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(InstitutionMailTemplate, ids, gqlFields),
});

export const getAllInstitutionMailTemplates = registerQuery({
  operationName: 'getAllInstitutionMailTemplates',
  type: () => [InstitutionMailTemplate],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<InstitutionMailTemplate>>(gqlFields: SelSet) =>
      readAllEntities(InstitutionMailTemplate, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllInstitutionMailTemplates = registerSubscription({
  operationName: 'subscribeAllInstitutionMailTemplates',
  type: () => [InstitutionMailTemplate],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<InstitutionMailTemplate>>(gqlFields: SelSet) =>
      watchAllEntities(InstitutionMailTemplate, gqlFields, { skip, limit, excludeIDs }),
});

export const getInstitutionMailTemplateByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<InstitutionMailTemplate>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getInstitutionMailTemplatesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeInstitutionMailTemplateByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<InstitutionMailTemplate>>(gqlFields: SelSet) =>
    id ? subscribeInstitutionMailTemplatesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
